import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, MapPin, Phone, Facebook, Twitter, Instagram, Youtube } from 'lucide-react';
import { useAppSelector, useAppDispatch } from '../../hooks/redux';
import { fetchPlatformSettings } from '../../store/slices/settingsSlice';
import api from '../../services/api';
import { formatSupportPhoneDisplay, getSupportPhoneHref } from '../../utils/supportPhone';
import { BrandLockup } from '../common';

type FooterSectionKey = 'platform' | 'community' | 'legal';

interface CMSPage {
  slug: string;
  title: string;
  footer_section?: FooterSectionKey | 'none' | null;
}

const Footer: React.FC = () => {
  const dispatch = useAppDispatch();
  const currentYear = new Date().getFullYear();
  const { settings } = useAppSelector((state) => state.settings);
  const [cmsPages, setCmsPages] = useState<CMSPage[]>([]);

  useEffect(() => {
    dispatch(fetchPlatformSettings());
    api.get<CMSPage[]>('/cms/navigation')
      .then((res) => setCmsPages(res.data))
      .catch(() => {});
  }, [dispatch]);
  
  // Get dynamic values from settings
  const platformName = settings?.platform_name || 'Bid4Babes';
  const platformTagline = settings?.platform_tagline || '';
  const platformDescription = settings?.platform_description || 'The ultimate interactive gaming and Game-a-Rama platform with live streaming. Join thousands of players worldwide.';
  const supportEmail = settings?.support_email || 'info@bid4babes.con';
  const supportPhone = settings?.support_phone;
  const supportAddress = settings?.support_address;
  const supportCity = settings?.support_city;
  const supportState = settings?.support_state;
  const supportCountry = settings?.support_country;
  const formattedSupportPhone = formatSupportPhoneDisplay(supportPhone);
  const supportPhoneHref = getSupportPhoneHref(supportPhone);
  const facebookUrl = settings?.facebook_url || 'https://facebook.com';
  const twitterUrl = settings?.twitter_url || 'https://twitter.com';
  const instagramUrl = settings?.instagram_url || 'https://instagram.com';
  const youtubeUrl = settings?.youtube_url || 'https://youtube.com';
  
  // Build full address string
  const fullAddress = [supportAddress, supportCity, supportState, supportCountry].filter(Boolean).join(', ');

  const footerLinks = {
    platform: [
      { label: 'Game-a-Rama', to: '/game-a-rama' },
      { label: 'Live Streams', to: '/streams' },
      { label: 'Games', to: '/games' },
      { label: 'Token Exchange', to: '/otc' },
      { label: 'Advertise', to: '/ads' },
      { label: 'Subscriptions', to: '/subscriptions' },
    ],
    community: [
      { label: 'Social Feed', to: '/feed' },
      { label: 'Messages', to: '/messages' },
      { label: 'Help Center', to: '/help' },
      { label: 'FAQ', to: '/faq' },
      { label: 'Contact Us', to: '/contact' },
    ],
    legal: [
      { label: 'Privacy Policy', to: '/page/privacy-policy' },
      { label: 'Terms of Service', to: '/page/terms-of-service' },
      { label: 'Cookie Policy', to: '/page/cookie-policy' },
      { label: 'Safety Guidelines', to: '/page/safety-guidelines' },
      { label: 'Compliance', to: '/page/compliance' },
      { label: 'Account Deletion', to: '/page/account-deletion' },
    ],
  };

  // Slugs already shown in hardcoded sections — don't duplicate them
  const hardcodedSlugs = new Set([
    'privacy-policy', 'terms-of-service', 'cookie-policy',
    'safety-guidelines', 'compliance', 'account-deletion', 'become-a-model',
    'help-center', 'faq', 'contact-us',
  ]);

  const dynamicCmsLinks = cmsPages
    .filter((p) => !hardcodedSlugs.has(p.slug))
    .reduce<Record<FooterSectionKey, { label: string; to: string }[]>>(
      (sections, page) => {
        const section = page.footer_section || 'legal';
        if (section !== 'none') {
          sections[section].push({ label: page.title, to: `/page/${page.slug}` });
        }
        return sections;
      },
      { platform: [], community: [], legal: [] }
    );

  const socialLinks = [
    { icon: Facebook, href: facebookUrl, label: 'Facebook', color: 'hover:text-blue-500' },
    { icon: Twitter, href: twitterUrl, label: 'Twitter', color: 'hover:text-cyan-500' },
    { icon: Instagram, href: instagramUrl, label: 'Instagram', color: 'hover:text-pink-500' },
    { icon: Youtube, href: youtubeUrl, label: 'YouTube', color: 'hover:text-red-500' },
  ];

  return (
    <footer className="relative bg-gradient-to-br from-gray-50 via-white to-gray-50 dark:from-[#0a0e27] dark:via-[#0d1333] dark:to-[#0a0e27] text-gray-900 dark:text-white overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-emerald-500 rounded-full mix-blend-multiply filter blur-3xl animate-float" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-teal-500 rounded-full mix-blend-multiply filter blur-3xl animate-float" style={{ animationDelay: '2s' }} />
      </div>

      <div className="relative">
        {/* Main Footer Content */}
        <div className="container-custom py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 mb-12">
            {/* Brand Section */}
            <div className="lg:col-span-2">
              <Link to="/" className="flex items-center gap-3 mb-6 group">
                <motion.div whileHover={{ scale: 1.05 }} transition={{ duration: 0.3 }}>
                  <BrandLockup
                    logoUrl={settings?.logo_url}
                    platformName={platformName}
                    platformTagline={platformTagline}
                    size="md"
                  />
                </motion.div>
              </Link>
              <p className="text-gray-700 dark:text-gray-400 mb-6 leading-relaxed">
                {platformDescription}
              </p>
              <div className="flex gap-3">
                {socialLinks.map((social) => (
                  <motion.a
                    key={social.label}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    whileHover={{ scale: 1.1, y: -2 }}
                    whileTap={{ scale: 0.9 }}
                    className={`p-3 bg-gray-50 dark:bg-[#0d1333] border border-gray-200 dark:border-[#1e2a5e] rounded-xl ${social.color} transition-all`}
                    aria-label={social.label}
                  >
                    <social.icon className="w-5 h-5" />
                  </motion.a>
                ))}
              </div>
            </div>

            {/* Platform */}
            <div>
              <h3 className="text-lg font-bold mb-4 bg-gradient-to-r from-emerald-400 to-teal-400 bg-clip-text text-transparent">
                Platform
              </h3>
              <ul className="space-y-3">
                {[...footerLinks.platform, ...dynamicCmsLinks.platform].map((link) => (
                  <li key={link.to}>
                    <Link to={link.to} className="text-gray-700 dark:text-gray-400 hover:text-emerald-500 dark:hover:text-emerald-400 transition-colors inline-flex items-center gap-2 group text-sm">
                      <span className="w-0 group-hover:w-2 h-0.5 bg-gradient-to-r from-emerald-500 to-teal-500 transition-all" />
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Community */}
            <div>
              <h3 className="text-lg font-bold mb-4 bg-gradient-to-r from-teal-400 to-cyan-400 bg-clip-text text-transparent">
                Community
              </h3>
              <ul className="space-y-3">
                {[...footerLinks.community, ...dynamicCmsLinks.community].map((link) => (
                  <li key={link.to}>
                    <Link to={link.to} className="text-gray-700 dark:text-gray-400 hover:text-teal-500 dark:hover:text-teal-400 transition-colors inline-flex items-center gap-2 group text-sm">
                      <span className="w-0 group-hover:w-2 h-0.5 bg-gradient-to-r from-teal-500 to-cyan-500 transition-all" />
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h3 className="text-lg font-bold mb-4 bg-gradient-to-r from-cyan-400 to-emerald-400 bg-clip-text text-transparent">
                Legal
              </h3>
              <ul className="space-y-3">
                {[...footerLinks.legal, ...dynamicCmsLinks.legal].map((link) => (
                  <li key={link.to}>
                    <Link to={link.to} className="text-gray-700 dark:text-gray-400 hover:text-cyan-500 dark:hover:text-cyan-400 transition-colors inline-flex items-center gap-2 group text-sm">
                      <span className="w-0 group-hover:w-2 h-0.5 bg-gradient-to-r from-cyan-500 to-emerald-500 transition-all" />
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Contact Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 py-8 border-t border-gray-200 dark:border-[#1e2a5e]">
            {supportEmail && (
              <motion.a
                href={`mailto:${supportEmail}`}
                whileHover={{ scale: 1.02 }}
                className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-[#0d1333] border border-gray-200 dark:border-[#1e2a5e] rounded-xl"
              >
                <div className="p-3 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-lg">
                  <Mail className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-sm text-gray-700 dark:text-gray-400">Email Us</p>
                  <p className="font-semibold break-words">{supportEmail}</p>
                </div>
              </motion.a>
            )}

            {supportPhone && supportPhoneHref && (
              <motion.a
                href={supportPhoneHref}
                whileHover={{ scale: 1.02 }}
                className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-[#0d1333] border border-gray-200 dark:border-[#1e2a5e] rounded-xl"
              >
                <div className="p-3 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-lg">
                  <Phone className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-sm text-gray-700 dark:text-gray-400">Call Us</p>
                  <p className="font-semibold break-words">{formattedSupportPhone}</p>
                </div>
              </motion.a>
            )}

            {fullAddress && (
              <motion.div
                whileHover={{ scale: 1.02 }}
                className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-[#0d1333] border border-gray-200 dark:border-[#1e2a5e] rounded-xl"
              >
                <div className="p-3 bg-gradient-to-br from-cyan-500 to-emerald-500 rounded-lg">
                  <MapPin className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-sm text-gray-700 dark:text-gray-400">Visit Us</p>
                  <p className="font-semibold break-words">{fullAddress}</p>
                </div>
              </motion.div>
            )}
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-200 dark:border-[#1e2a5e]">
          <div className="container-custom py-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <p className="text-gray-700 dark:text-gray-400 text-sm text-center md:text-left">
                © {currentYear} {platformName}. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
